import React from 'react';
import { isEmpty, map } from 'lodash';
import cx from 'classnames';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import InspectorFilter from '../elements/schema-editor/schema-inspector/InspectorFilter';
import SchemaValidationsEditorContainer from '../elements/schema-editor/schema-inspector/SchemaValidationsEditorContainer';
import Tooltip, { DefaultTooltipPopperProps } from '../elements/mui/Tooltip';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-sr-cross-circle.svg';
import TransformRecipeGroup from './TransformRecipeGroup';
import TransformCodeRecipeGroup from './TransformCodeRecipeGroup';
import AutoMappingRecipeGroup from './AutoMappingRecipeGroup';
import DefaultRecipeGroup from './DefaultRecipeGroup';
import AnnotationsRecipeGroup from './AnnotationsRecipeGroup';
import Constants from '../../Constants';
import { connect } from 'react-redux';
import { setExpandedRecipe, setExpandedRecipeGroup, setRecipeGroups } from 'js/actions';
import {
  RECIPE_GROUP_ANNOTATIONS,
  RECIPE_GROUP_DEFAULTS,
  RECIPE_GROUP_FILTERS,
  RECIPE_GROUP_LABELS,
  RECIPE_GROUP_TRANSFORMS,
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_CODE,
  RECIPE_GROUP_TRANSFORMS_REPLICATE,
  RECIPE_GROUP_VALIDATIONS,
} from 'js/constants/schemaEditor';
import UIUtils from '../../lib/UIUtils';
import { FLOW_TYPE_SPARK } from 'js/constants/pipelineConstants';
import { ChipWithIcon } from 'new/elements/Chip';
import { ChipBadge } from 'new/elements/ChipBadge';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { TwoStepDeleteButton } from 'new/elements/TwoStepDeleteButton/TwoStepDeleteButton';
import { checkIsPython3Enabled } from 'new/constants/featureFlags';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';
/**
 * Icons
 */
import { ReactComponent as ChevronIcon } from 'new/components/icons/chevron_right.svg';
import { ReactComponent as TransformIcon } from 'new/components/icons/fi-rr-tool-crop.svg';
import { ReactComponent as ReusableIcon } from 'new/components/icons/fi-rr-recycle.svg';
import { ReactComponent as EyeIcon } from 'new/components/icons/fi-rr-eye.svg';
import { ReactComponent as EyeSlashIcon } from 'new/components/icons/fi-rr-eye-crossed.svg';
import { ReactComponent as DragHandleIcon } from 'new/components/icons/burger.svg';
/**
 * Styles - CSS
 */
import './RecipeBuilder.scss';
import s from './RecipeGroupsList.module.scss';
/**
 * Feature Flags
 */

const PYTHON3_LANGUAGE_OPTION = {
  [Constants.CodeLanguages.PYTHON3]: {
    label: 'Python 3 (Experimental)',
    value: Constants.CodeLanguages.PYTHON3,
    type: 'custom_code',
  },
};

const PYTHON_LANGUAGE_OPTION = {
  [Constants.CodeLanguages.PYTHON]: {
    label: 'Python',
    value: Constants.CodeLanguages.PYTHON,
    type: 'custom_code',
  },
};

const LANGUAGE_OPTIONS = {
  ...PYTHON_LANGUAGE_OPTION,
  ...PYTHON3_LANGUAGE_OPTION,
  [Constants.CodeLanguages.JAVASCRIPT]: {
    label: 'JavaScript',
    value: Constants.CodeLanguages.JAVASCRIPT,
    type: 'custom_code',
  },
  [Constants.CodeLanguages.JOLT]: {
    label: 'Nexla JSON',
    value: Constants.CodeLanguages.JOLT,
    type: 'custom_code',
  },
  [Constants.CodeLanguages.FLINK_SQL]: {
    label: 'SQL',
    value: Constants.CodeLanguages.FLINK_SQL,
    type: 'custom_code',
    description:
      'Write SQL queries for executing transforms. Query statements must be compliant with Apache Flink SQL syntax.',
  },
  [Constants.CodeLanguages.SPARK_SQL]: {
    label: 'SQL',
    value: Constants.CodeLanguages.SPARK_SQL,
    type: 'custom_code',
  },
};

const TRANSFORM_CODE_RECIPE_GROUP_LANGUAGE_OPTIONS = [
  Constants.CodeLanguages.PYTHON,
  Constants.CodeLanguages.JAVASCRIPT,
  Constants.CodeLanguages.JOLT,
];

//TODO: This is temporary till we make Python 3 the default language.
const TRANSFORM_CODE_RECIPE_GROUP_LANGUAGE_OPTIONS_PYTHON3 = [
  Constants.CodeLanguages.PYTHON,
  Constants.CodeLanguages.PYTHON3,
  Constants.CodeLanguages.JAVASCRIPT,
  Constants.CodeLanguages.JOLT,
];

const GenericTooltip = ({ title, children }) => {
  return (
    <Tooltip
      interactive="true"
      placement="bottom"
      tooltipTheme="fieldInfo"
      PopperPros={DefaultTooltipPopperProps}
      title={title}
      children={children}
    />
  );
};

class RecipeGroupsList extends React.PureComponent {
  expandRecipeGroupClicked = (index = -1) => {
    const { isReadOnly } = this.props;
    if (isReadOnly) return;
    this._handleExpandRecipeGroupClicked(index);
  };

  render() {
    const {
      recipeGroups,
      expandedRecipeGroupIndex,
      isReadOnly,
      isPython3Enabled,
      isMultiBlockEnabled = false,
    } = this.props;

    const isInteractive = !isReadOnly;

    // TEMPORARY: When multi-block is disabled, prevent reordering and disabling blocks
    // Remove this after full rollout (few weeks)
    const canReorderBlocks = isInteractive && isMultiBlockEnabled;
    const canDisableBlocks = isInteractive && isMultiBlockEnabled;

    // if no recipe group exists, exit
    if (isEmpty(recipeGroups)) {
      return null;
    }

    const lastTypeIndex = {};
    const labelSuffixes = map(recipeGroups, ({ type }) => {
      lastTypeIndex[type] = (lastTypeIndex[type] || 0) + 1;
      return lastTypeIndex[type];
    });

    return (
      <DragDropContext onDragEnd={this._handleDragEnd}>
        <Droppable droppableId="recipe-groups-list" isDropDisabled={!canReorderBlocks}>
          {(provided, snapshot) => (
            <ol className="recipe-groups-list" ref={provided.innerRef} {...provided.droppableProps}>
              {map(recipeGroups, (recipeGroup, index) => {
                const { type } = recipeGroup;
                const isExpanded = expandedRecipeGroupIndex === index;
                const label = RECIPE_GROUP_LABELS[type] + ` #${labelSuffixes[index]}`;
                const summaryText = this._getRecipeGroupSummary(recipeGroup) || null;

                const isDisabled = recipeGroup.disabled || false;
                const containerClass = cx(
                  isInteractive ? 'recipe-group' : s.recipe_group_read_mode,
                  `recipe-group-type-${type}`,
                  isExpanded && 'is-expanded',
                  isReadOnly && 'is-read-only',
                  // Disabled styling:
                  // - Edit mode: global 'is-disabled' string (legacy CSS in RecipeBuilder.scss)
                  // - Read-only mode: CSS Module 's.is_disabled' (modern scoped styles)
                  isDisabled && (isReadOnly ? s.is_disabled : 'is-disabled'),
                );

                const headerClass = isReadOnly ? s.read_mode_header : 'recipe-group-header';

                return (
                  <Draggable
                    key={`recipe-group-${index}`}
                    draggableId={`recipe-group-${index}`}
                    index={index}
                    isDragDisabled={!canReorderBlocks}
                  >
                    {(provided, snapshot) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={cx(containerClass, {
                          'is-dragging': snapshot.isDragging,
                        })}
                      >
                        <div className={headerClass}>
                          {canReorderBlocks ? (
                            <div className="recipe-group-drag-handle" {...provided.dragHandleProps}>
                              <GenericTooltip title="Drag to reorder">
                                <div className="drag-handle-icon">
                                  <DragHandleIcon width={16} height={16} />
                                </div>
                              </GenericTooltip>
                            </div>
                          ) : null}

                          <div
                            className={
                              isReadOnly ? s.read_mode_title : 'recipe-group-title-container'
                            }
                            onClick={() => this.expandRecipeGroupClicked(index)}
                          >
                            {isInteractive ? (
                              <GenericTooltip
                                title={isExpanded ? 'Hide' : 'Expand'}
                                children={
                                  <ChevronIcon
                                    width={18}
                                    height={18}
                                    className={isExpanded ? 'chevron-top' : 'chevron-bottom'}
                                  />
                                }
                              />
                            ) : null}

                            <span>{label}</span>

                            {isReadOnly && summaryText ? <span>&raquo;</span> : null}

                            {summaryText ? (
                              isReadOnly ? (
                                <span>{summaryText}</span>
                              ) : (
                                <div className="recipe-group-summary">{summaryText}</div>
                              )
                            ) : null}

                            {isReadOnly ? (
                              <div className={s.readOnlyContainer}>
                                {recipeGroup.type.includes('tx') ? (
                                  <ChipWithIcon label="Transform" icon={<TransformIcon />} />
                                ) : null}

                                {recipeGroup.data.type === 'reusable_tx' ? (
                                  <ChipWithIcon label="Reusable" icon={<ReusableIcon />} />
                                ) : null}

                                {recipeGroup.data.codeLanguage ? (
                                  <ChipBadge
                                    label={
                                      LANGUAGE_OPTIONS[recipeGroup.data.codeLanguage]?.label ??
                                      recipeGroup.data.codeLanguage
                                    }
                                  />
                                ) : null}

                                {recipeGroup.data?.transformId ? (
                                  <ChipWithCopy toCopy={recipeGroup.data?.transformId}>
                                    ID {recipeGroup.data?.transformId}
                                  </ChipWithCopy>
                                ) : null}
                              </div>
                            ) : null}
                          </div>

                          {isInteractive ? (
                            <div className="recipe-group-cta-container">
                              {/* Disable/Enable Toggle - TEMPORARY: Only show when multi-block enabled */}
                              {canDisableBlocks ? (
                                <GenericTooltip
                                  title={isDisabled ? 'Enable Block' : 'Disable Block'}
                                >
                                  <button
                                    className="recipe-group-action-button"
                                    onClick={() => this._handleToggleRecipeGroupDisabled(index)}
                                    aria-label={isDisabled ? 'Enable group' : 'Disable group'}
                                  >
                                    {isDisabled ? (
                                      <EyeSlashIcon width={16} height={16} />
                                    ) : (
                                      <EyeIcon width={16} height={16} />
                                    )}
                                  </button>
                                </GenericTooltip>
                              ) : null}

                              {/* Delete Button */}
                              <TwoStepDeleteButton
                                index={index}
                                onDeleteGroup={this._handleDeleteRecipeGroupClicked}
                              />
                            </div>
                          ) : null}
                        </div>

                        <div className="recipe-group-content">
                          {this._renderRecipeGroup(recipeGroup, index, isExpanded || isReadOnly)}
                        </div>
                      </li>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </ol>
          )}
        </Droppable>
      </DragDropContext>
    );
  }

  _getRecipeGroupSummary = (recipeGroup) => {
    const { type, data } = recipeGroup;

    switch (type) {
      case RECIPE_GROUP_TRANSFORMS:
      case RECIPE_GROUP_DEFAULTS:
      case RECIPE_GROUP_FILTERS:
      case RECIPE_GROUP_VALIDATIONS:
      case RECIPE_GROUP_ANNOTATIONS:
        return UIUtils.toPluralFormat('Rule', data?.length, true);

      case RECIPE_GROUP_TRANSFORMS_CODE:
      case RECIPE_GROUP_TRANSFORMS_AGGREGATE:
        if (data.type === 'custom_code') {
          return (
            LANGUAGE_OPTIONS[data.codeLanguage] || LANGUAGE_OPTIONS[Constants.CodeLanguages.JOLT]
          ).label;
        }

        const { transforms } = this.props;
        const transform = transforms[data.transformId];

        return transform?.name;

      case RECIPE_GROUP_TRANSFORMS_REPLICATE:
        return data ? 'ON' : 'OFF';

      default:
        return null;
    }
  };

  _renderRecipeGroup = (recipeGroup, index, isExpanded) => {
    const {
      dataSet,
      recipeGroups,
      filterDisplayOptions,
      defaultsDisplayOptions,
      outputSchema,
      isReadOnly,
      isInteractive,
      onLoadReusableTransforms,
      onApplyRecipe,
      isPython3Enabled,
    } = this.props;
    const { type, data } = recipeGroup;
    const commonProps = {
      type,
      data,
      isReadOnly,
      isExpanded,
      isInteractive,
      onExpand: () => this._handleExpandRecipeGroupClicked(index),
      onRecipeClicked: (recipeIndex) => this._handleRecipeClicked(index, recipeIndex),
      onChangeRecipeGroup: (data) => this._handleChangeRecipeGroup(data, index),
    };

    // Check if other Tx blocks exist (for Reusable Transform restriction)
    const hasOtherTxBlocks = recipeGroups.some(
      (group, groupIndex) =>
        groupIndex !== index &&
        [
          RECIPE_GROUP_TRANSFORMS,
          RECIPE_GROUP_TRANSFORMS_CODE,
          RECIPE_GROUP_TRANSFORMS_AGGREGATE,
        ].includes(group.type),
    );

    switch (type) {
      case RECIPE_GROUP_TRANSFORMS:
        return (
          <TransformRecipeGroup
            {...commonProps}
            dataSet={dataSet}
            onLoadReusableTransforms={onLoadReusableTransforms}
            onApplyRecipe={onApplyRecipe}
          />
        );

      case RECIPE_GROUP_DEFAULTS:
        return (
          <DefaultRecipeGroup
            {...commonProps}
            outputSchema={outputSchema}
            displayOptions={defaultsDisplayOptions}
          />
        );

      case RECIPE_GROUP_FILTERS:
        return (
          <InspectorFilter
            {...commonProps}
            filters={data[0]}
            outputSchema={outputSchema}
            displayOptions={filterDisplayOptions}
            onFilterChanged={(filterGroup) => this._handleChangeRecipeGroup([filterGroup], index)}
          />
        );

      case RECIPE_GROUP_TRANSFORMS_CODE: {
        const effectivePython3Enabled = isPython3Enabled && !isCapsuleCluster(dataSet);
        const languageOptions = effectivePython3Enabled
          ? map(
              TRANSFORM_CODE_RECIPE_GROUP_LANGUAGE_OPTIONS_PYTHON3,
              (language) => LANGUAGE_OPTIONS[language],
            )
          : map(
              TRANSFORM_CODE_RECIPE_GROUP_LANGUAGE_OPTIONS,
              (language) => LANGUAGE_OPTIONS[language],
            );

        return (
          <TransformCodeRecipeGroup
            {...commonProps}
            languageOptions={languageOptions}
            onLoadReusableTransforms={onLoadReusableTransforms}
            hasOtherTxBlocks={hasOtherTxBlocks}
          />
        );
      }

      case RECIPE_GROUP_TRANSFORMS_AGGREGATE:
        if (isCapsuleCluster(dataSet)) return null;

        return (
          <TransformCodeRecipeGroup
            {...commonProps}
            languageOptions={[
              LANGUAGE_OPTIONS[
                dataSet?.flow_type === FLOW_TYPE_SPARK
                  ? Constants.CodeLanguages.SPARK_SQL
                  : Constants.CodeLanguages.FLINK_SQL
              ],
            ]}
            onLoadReusableTransforms={onLoadReusableTransforms}
          />
        );

      case RECIPE_GROUP_TRANSFORMS_REPLICATE:
        return <AutoMappingRecipeGroup {...commonProps} />;

      case RECIPE_GROUP_VALIDATIONS:
        return <SchemaValidationsEditorContainer {...commonProps} />;

      case RECIPE_GROUP_ANNOTATIONS:
        return <AnnotationsRecipeGroup {...commonProps} outputSchema={outputSchema} />;

      default:
        return null;
    }
  };

  /**
   * recipeGroups: @type {Array<RecipeGroup>}
   * @example
   * [
   *   {
   *     type: string,
   *     data: object || array
   *   }
   * ]
   */
  _handleChangeRecipeGroup = (data, index) => {
    const { recipeGroups, setRecipeGroups } = this.props;
    const newRecipeGroups = [...recipeGroups];
    newRecipeGroups[index] = { ...newRecipeGroups[index], data };

    setRecipeGroups(newRecipeGroups, true);
  };

  _handleDeleteRecipeGroupClicked = (index) => {
    const { recipeGroups, setRecipeGroups } = this.props;
    const newRecipeGroups = [...recipeGroups];
    newRecipeGroups.splice(index, 1);
    setRecipeGroups(newRecipeGroups, true);
  };

  _handleExpandRecipeGroupClicked = (index) => {
    const { expandedRecipeGroupIndex, setExpandedRecipeGroup, setExpandedRecipe } = this.props;

    setExpandedRecipeGroup(index === expandedRecipeGroupIndex ? -1 : index);
    setExpandedRecipe(-1);
  };

  _handleRecipeClicked = (recipeGroupIndex, recipeIndex) => {
    this.props.onRecipeClicked(recipeGroupIndex, recipeIndex);
  };

  /**
   * _handleToggleRecipeGroupDisabled - Toggles the disabled state of a recipe group
   *
   * Disabled blocks are skipped during JOLT generation, making it easy to
   * experiment with different configurations without deleting blocks.
   */
  _handleToggleRecipeGroupDisabled = (index) => {
    const { recipeGroups, setRecipeGroups } = this.props;
    const newRecipeGroups = [...recipeGroups];
    newRecipeGroups[index] = {
      ...newRecipeGroups[index],
      disabled: !newRecipeGroups[index].disabled,
    };

    setRecipeGroups(newRecipeGroups, true);
  };

  /**
   * _handleDragEnd - Handles the end of a drag operation to reorder recipe groups
   *
   * Reordering blocks can change whether a block is builder-first or code-first,
   * which affects scaffolding behavior in the generated JOLT.
   */
  _handleDragEnd = (result) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }

    const { recipeGroups, setRecipeGroups, expandedRecipeGroupIndex, setExpandedRecipeGroup } =
      this.props;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // No movement
    if (sourceIndex === destinationIndex) {
      return;
    }

    // Reorder the array
    const newRecipeGroups = Array.from(recipeGroups);
    const [removed] = newRecipeGroups.splice(sourceIndex, 1);
    newRecipeGroups.splice(destinationIndex, 0, removed);

    // Update expanded index if the expanded block was moved
    if (expandedRecipeGroupIndex === sourceIndex) {
      setExpandedRecipeGroup(destinationIndex);
    } else if (
      sourceIndex < expandedRecipeGroupIndex &&
      destinationIndex >= expandedRecipeGroupIndex
    ) {
      setExpandedRecipeGroup(expandedRecipeGroupIndex - 1);
    } else if (
      sourceIndex > expandedRecipeGroupIndex &&
      destinationIndex <= expandedRecipeGroupIndex
    ) {
      setExpandedRecipeGroup(expandedRecipeGroupIndex + 1);
    }

    setRecipeGroups(newRecipeGroups, true);
  };
}

const mapStateToProps = (state) => {
  const {
    entities: { transforms },
    recipeBuilder: { expandedRecipeGroupIndex },
    schemaEditor: { filterDisplayOptions, defaultsDisplayOptions },
  } = state;

  const isPython3Enabled = checkIsPython3Enabled();

  return {
    isPython3Enabled,
    transforms,
    filterDisplayOptions,
    defaultsDisplayOptions,
    expandedRecipeGroupIndex,
  };
};

export default connect(mapStateToProps, {
  setRecipeGroups,
  setExpandedRecipeGroup,
  setExpandedRecipe,
})(RecipeGroupsList);
